import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Trash2, Save, Loader2, Search, ChevronLeft, ArrowUpDown, Info,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import ToastMsg from '../../../components/common/ToastMsg';
import { cn } from '../../../utils/tailwindUtils';


export default function DepartmentARPPSettings() {
  const navigate = useNavigate();
  const { fetch: apiFetch, update: apiUpdate } = useApiMethods({ throwOnFail: true });

  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('department');
  const [sortDir, setSortDir] = useState('asc');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch('engage/analytics/department-arpp/');
        const list = resp?.data || resp?.data?.data || [];
        setRows(Array.isArray(list) ? list : []);
      } catch {
        setError('Failed to load overrides');
        setRows([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRow    = () => { setRows(r => [...(r || []), { _new: true, department: '', avg_arpp: 0, notes: '' }]); setDirty(true); };
  const removeRow = (i) => { setRows(r => r.filter((_, idx) => idx !== i)); setDirty(true); };
  const updateRow = (i, patch) => { setRows(r => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row))); setDirty(true); };

  const filteredIdx = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    const withIndex = rows.map((r, i) => ({ r, i }));
    const filtered = q
      ? withIndex.filter(({ r }) => (r.department || '').toLowerCase().includes(q) || (r.notes || '').toLowerCase().includes(q))
      : withIndex;
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered.sort(({ r: a }, { r: b }) => {
      let x = a[sortKey], y = b[sortKey];
      if (sortKey === 'avg_arpp') { x = Number(x || 0); y = Number(y || 0); return (x - y) * dir; }
      x = (x || '').toString().toLowerCase(); y = (y || '').toString().toLowerCase();
      return x < y ? -1 * dir : x > y ? 1 * dir : 0;
    });
  }, [rows, search, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };

  const save = async () => {
    const seen = new Set();
    for (const r of (rows || [])) {
      const key = (r.department || '').trim().toLowerCase();
      if (!key) continue;
      if (seen.has(key)) {
        ToastMsg('error', `Duplicate department: "${r.department}"`);
        return;
      }
      seen.add(key);
      if (Number(r.avg_arpp) < 0) {
        ToastMsg('error', `ARPP must be non-negative for ${r.department}`);
        return;
      }
    }
    setSaving(true);
    try {
      const cleaned = (rows || []).filter(r => (r.department || '').trim()).map(({ _new, ...rest }) => rest);
      const resp = await apiUpdate('engage/analytics/department-arpp/', { rows: cleaned });
      const list = resp?.data || resp?.data?.data || [];
      setRows(Array.isArray(list) ? list : []);
      setDirty(false);
      ToastMsg('success', `Saved ${cleaned.length} department override${cleaned.length !== 1 ? 's' : ''}`);
    } catch (e) {
      ToastMsg('error', 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 max-w-[1000px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Department ARPP overrides</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Per-department averages used when an enquiry doesn't have a specific quote or a catalog match.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">Unsaved changes</span>}
          <button onClick={addRow}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/30">
            <Plus className="w-3.5 h-3.5" /> Add department
          </button>
          <button onClick={save} disabled={saving || !rows}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save overrides'}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by department or note…"
            className="w-full pl-10 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <ThSort label="Department"    onClick={() => toggleSort('department')} active={sortKey === 'department'} dir={sortDir} />
              <ThSort label="Average ARPP"  onClick={() => toggleSort('avg_arpp')}   active={sortKey === 'avg_arpp'}   dir={sortDir} right />
              <th className="px-4 py-2 text-left font-medium">Notes</th>
              <th className="px-4 py-2 text-center font-medium w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows === null ? (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading…
              </td></tr>
            ) : filteredIdx.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                {search ? `No overrides match "${search}"` : 'No overrides yet. Click "Add department" to start.'}
              </td></tr>
            ) : filteredIdx.map(({ r, i }) => (
              <tr key={i} className={cn('hover:bg-gray-50 dark:hover:bg-gray-700/40', r._new && 'bg-emerald-50/40 dark:bg-emerald-900/10')}>
                <td className="px-4 py-2">
                  <input type="text" value={r.department || ''}
                    onChange={(e) => updateRow(i, { department: e.target.value })}
                    placeholder="Cardiology"
                    className="w-full px-2 py-1 text-sm rounded bg-transparent border border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-emerald-500 focus:bg-white dark:focus:bg-gray-900 focus:outline-none text-gray-900 dark:text-white font-medium" />
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="relative inline-block">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
                    <input type="number" min="0" step="500" value={r.avg_arpp ?? 0}
                      onChange={(e) => updateRow(i, { avg_arpp: Number(e.target.value) || 0 })}
                      className="w-32 pl-6 pr-2 py-1 text-sm text-right rounded bg-transparent border border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-emerald-500 focus:bg-white dark:focus:bg-gray-900 focus:outline-none text-gray-900 dark:text-white font-semibold tabular-nums" />
                  </div>
                </td>
                <td className="px-4 py-2">
                  <input type="text" value={r.notes || ''}
                    onChange={(e) => updateRow(i, { notes: e.target.value })}
                    placeholder="Optional context…"
                    className="w-full px-2 py-1 text-sm rounded bg-transparent border border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-emerald-500 focus:bg-white dark:focus:bg-gray-900 focus:outline-none text-gray-600 dark:text-gray-400" />
                </td>
                <td className="px-4 py-2 text-center">
                  <button onClick={() => removeRow(i)}
                    className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800/40 text-[11px] text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <p>
          <b>Value resolution priority:</b> per-enquiry quote → matching procedure catalog default → this department override → global ARPP. Match is case-insensitive on <code>enquiry.department_of_interest</code>.
        </p>
      </div>
    </div>
  );
}


function ThSort({ label, onClick, active, dir, right }) {
  return (
    <th className={cn('px-4 py-2 font-medium', right ? 'text-right' : 'text-left')}>
      <button onClick={onClick} className={cn(
        'inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors',
        active && 'text-gray-900 dark:text-white',
      )}>
        {label}
        <ArrowUpDown className={cn('w-3 h-3', active ? 'text-emerald-500' : 'text-gray-300')} />
        {active && <span className="text-[8px]">{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}
